import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {Product} from "../../../../model/product";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import NameFinderUtils from "../../../../utils/name-finder-utils";
import FormUtils from "../../../../utils/form-utils";
import {Property} from "../../../../model/property";

@Component({
  selector: 'app-product-value-form',
  templateUrl: './product-value-form.component.html',
  styleUrls: ['./product-value-form.component.css']
})
export class ProductValueFormComponent implements OnInit {

  @Input() products: Product[];
  @Input() hasPadding: boolean = true;
  @Input() displayCancelButton: boolean = true;
  @Input() displayConfirmButton: boolean = true;
  @Input() displayHeader: boolean = true;
  @Input() displayDocName: boolean = true
  @Input() cancelButtonText: string = "Abbrechen"
  @Input() confirmButtonText: string = "Bestätigen"
  @Input() property: Property;
  @Output() cancelModalEvent = new EventEmitter();
  @Output() confirmModalEvent = new EventEmitter();
  productValueForm: FormGroup;
  selectedProperty: Property;

  protected readonly NameFinderUtils = NameFinderUtils;
  protected readonly FormUtils = FormUtils;
  constructor(private readonly formBuilder: FormBuilder,
              private readonly dialogRef: MatDialogRef<ProductValueFormComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any) {
  }

  /**
   * Initializes the component by setting up the product list, selected property and
   * generating a dynamic form based on the product values
   *
   * - Falls back to injected data if 'products' or 'selectedProperty' are not explicitly passed in
   *   (when using this in a dialog instead a standalone component)
   */
  ngOnInit(): void {
    this.products = this.products?.length ? this.products : this.data?.products || [];
    this.selectedProperty = this.property ? this.property : this.data?.selectedProperty ?? undefined;
    this.productValueForm = this.formBuilder.group({})
    const additionalProductValues = this.selectedProperty?.additionalProductValues ?? {}

    this.products.forEach(product => {
      product.productValues.forEach(productValue => {
        const validators = [];

        // Add required validator if applicable
        if (productValue.required) {
          validators.push(Validators.required);
        }

        // Add pattern validator if validatorRegex is defined
        if (productValue.validatorRegex) {
          validators.push(Validators.pattern(productValue.validatorRegex));
        }

        const prefillValue = additionalProductValues[productValue.key] ?? '';

        // Add control with combined validators
        this.productValueForm.addControl(
            productValue.key,
            this.formBuilder.control(prefillValue, validators)
        );
      })
    })
  }

  /**
   * Cancels the modal. Uses the 'useDialogFunctionality' to determine how the close functionality should be handled
   * - If this component is used within a component the dialog is closed, emitting 'false'
   * - If the component is used as a standalone component emits a custom event to signal cancellation
   */
  public cancelModal(): void{
    if(this.data?.useDialogFunctionality){
      this.dialogRef.close(false);
    }
    else{
      this.cancelModalEvent.emit();
    }
  }

  /**
   * Checks whether the product value form is valid.
   * If invalid triggers marking of invalid fields.
   *
   * @return True if the product value form is valid, else false
   */
  public isProductValueFormValid(): boolean{
    const isFormValid = this.productValueForm.valid;
    if(!isFormValid){
      FormUtils.markInvalidInputs(this.productValueForm);
    }
    return isFormValid;
  }

  /**
   * Handles submission of the modal form.
   * - Adds data from the form to the selected property 'additionalProductValues'
   * - Closes the modal or emit a confirmation event depending on the dialog mode
   */
  public submitModal(): void{
    // Check validity before proceeding
    if(!this.isProductValueFormValid()){
      return;
    }

    const formValues = this.productValueForm.value;
    if(this.selectedProperty){
      if(!this.selectedProperty.additionalProductValues){
        this.selectedProperty.additionalProductValues = {};
      }

      // Add values from form to the selectedProperty
      Object.entries(formValues).forEach(([key, value]) => {
        if (value) {
          this.selectedProperty.additionalProductValues[key] = String(value);
        }
      });
    }

    // Close the dialog if this component is used within a dialog
    if(this.data?.useDialogFunctionality){
      this.dialogRef.close(true);
    }
    else{
      // Emit a confirmation event if this component is used as a standalone component
      this.confirmModalEvent.emit();
    }
  }
}
